import { useMutation, useQueryClient } from '@tanstack/react-query';

import { isDefined, isEmpty, isNotDefined, isOfType } from '@hsp/utilities';

import { useAppSelector } from 'hooks/store';
import { useAssignMailCode } from 'pages/audience/shared/save/hooks';
import { useSaveStoreSelector } from 'pages/audience/shared/save/hooks/context';
import { savePromptQueryKeys } from 'pages/audience/shared/save-prompt/utilities';
import { sharedABQueryKeys } from 'pages/audience/shared/utilities';
import { userService } from 'services';
import { selectUserId } from 'store/selectors';
import { IAudience, IAudienceShop, MultiRfmSearchApi } from 'types/api/audience';
import { IResult } from 'types/api/common';
import { SaveForm } from 'types/pages/audience-builder/shared/save';

export const useUpdateUserAudience = () => {
  const userId = useAppSelector(selectUserId);

  const { mutateAsync: assignMailCode } = useAssignMailCode();
  const queryClient = useQueryClient();
  const data = useSaveStoreSelector(({ data }) => data);

  const queryKey = savePromptQueryKeys.audiences(userId);

  return useMutation({
    mutationFn: async (formData: SaveForm) => {
      const { id, type } = formData;

      if (isNotDefined(id)) {
        return Promise.reject();
      }

      if (isNotDefined(userId)) {
        return Promise.reject();
      }

      if (type !== 'basic' || !isOfType<IAudienceShop>(data, 'categoryId')) {
        return Promise.reject();
      }

      const { shopId } = formData;

      if (isNotDefined(shopId)) {
        return Promise.reject();
      }

      const { client, description, giftDateEnd, giftDateStart, quantity, statusId, ...rest } = formData;
      const shop: IAudienceShop = {
        ...data,
        ...rest,
        clientId: client?.id ?? null,
        clientName: client?.name ?? null,
        description: isEmpty(description) ? null : description,
        giftDateEnd: giftDateEnd?.toISOString(),
        giftDateStart: giftDateStart?.toISOString(),
        id,
        quantity: quantity ?? 0,
        shopId,
        statusId,
        userId,
      };

      const { parameters } = shop;

      return assignMailCode({ id, orderNumber: null, shopId })
        .then(async ({ dataContainer, success }) => {
          if (success && isDefined(dataContainer)) {
            const { sequenceNumber } = dataContainer;

            formData.sequenceNumber = sequenceNumber;
          }

          if (isOfType<MultiRfmSearchApi>(parameters, 'rfmSets')) {
            return await userService.updateAudienceShopMultiRfm({ data: shop, userId });
          }

          return await userService.updateAudienceShop({ data: shop, userId });
        })
        .catch(() => Promise.reject());
    },
    onError: (_error, formData: SaveForm, context) => {
      const { type } = formData;

      if (type === 'basic') {
        const { shopId } = formData;

        if (isDefined(shopId)) {
          queryClient.setQueriesData({ queryKey: sharedABQueryKeys.shop.default(shopId) }, context?.shopSnapshot);
        }
      }
      queryClient.setQueriesData({ queryKey }, context?.audiencesSnapshot);
    },
    onMutate: async (formData: SaveForm) => {
      const { type } = formData;

      await queryClient.cancelQueries({ queryKey });

      const audiencesSnapshot = queryClient.getQueriesData<IResult<IAudience[]>>({ queryKey });

      if (type !== 'basic') {
        return { audiencesSnapshot };
      }

      const { shopId } = formData;

      if (isNotDefined(shopId)) {
        return { audiencesSnapshot };
      }

      const shopQueryKey = sharedABQueryKeys.shop.default(shopId);

      await queryClient.cancelQueries({ queryKey: shopQueryKey });

      const shopSnapshot = queryClient.getQueriesData<IResult<IAudienceShop>>({ queryKey: shopQueryKey });

      queryClient.setQueriesData<IResult<IAudienceShop>>({ queryKey: shopQueryKey }, (oldData) => {
        if (isDefined(oldData)) {
          const { dataContainer } = oldData;

          if (isNotDefined(dataContainer)) {
            return oldData;
          }

          const {
            client: { id: clientId, name: clientName },
            giftDateEnd,
            giftDateStart,
            ...shop
          } = formData;

          oldData = {
            ...oldData,
            dataContainer: {
              ...dataContainer,
              ...shop,
              clientId,
              clientName,
              giftDateEnd: giftDateEnd?.toISOString(),
              giftDateStart: giftDateStart?.toISOString(),
            },
          };

          return oldData;
        }

        return oldData;
      });

      queryClient.setQueriesData<IResult<IAudience[]>>({ queryKey }, (oldData) => {
        if (isDefined(oldData)) {
          let { dataContainer } = oldData;

          if (isNotDefined(dataContainer)) {
            return oldData;
          }

          const { giftDateEnd, giftDateStart } = formData;

          dataContainer = [
            ...dataContainer,
            {
              category: {
                id: formData.categoryId,
                name: '',
              },
              clientId: formData.client?.id ?? undefined,
              clientName: formData.client?.name ?? undefined,
              giftDateEnd: giftDateEnd?.toISOString(),
              giftDateStart: giftDateStart?.toISOString(),
              id: formData.id ?? '',
              name: formData.name,
              orderNumber: formData.orderNumber ?? undefined,
              quantity: formData.quantity,
              shops: [],
              userId,
            },
          ];

          oldData = {
            ...oldData,
            dataContainer: [...dataContainer],
          };

          return oldData;
        }

        return oldData;
      });

      return {
        audiencesSnapshot,
        shopSnapshot,
      };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onSuccess: (result, formData) => {
      if (result?.success) {
        if (isOfType<IAudienceShop>(formData, 'categoryId') && isDefined(formData.shopId)) {
          queryClient.invalidateQueries({ queryKey: sharedABQueryKeys.shop.default(formData.shopId) });
        }
      }
    },
  });
};
